import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Crown, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * AcceptInvite — public page that accepts a GOAT invite token (from email).
 * If the email is new, the user sets a password + name; if it's already
 * registered, accepting just links them to the owner's team.
 */
export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { finalizeGoogleSession } = useAuth();
  const token = params.get("token") || "";
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("This invite link is missing or broken.");
    }
  }, [token]);

  const accept = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/goat/invites/accept", { token, password, name });
      finalizeGoogleSession?.(r.data);
      toast.success("Welcome to the team, hero!");
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message;
      toast.error(msg || "Couldn't accept this invite.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hero-theme min-h-screen flex flex-col items-center justify-center px-5 py-10" data-testid="accept-invite-page">
      <div className="w-full max-w-md ht-card rounded-3xl p-7 shadow-lg ht-outline-gold">
        <div className="flex items-center gap-2 mb-3">
          <Crown className="w-5 h-5 text-[#F5D849]" strokeWidth={2.6} />
          <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#F5D849]">G.O.A.T Team Invite</span>
        </div>
        <h1
          className="text-3xl font-black text-white leading-tight"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Join the<br />Money Hero squad.
        </h1>
        <p className="mt-2 text-sm text-[#CDE0D9] leading-relaxed">
          You&apos;ve been invited to a G.O.A.T plan team. Set up your account below — you&apos;ll get the
          full hero toolkit (defeat bills, link banks, build your own Vault) at no cost while the
          plan is active.
        </p>

        <form onSubmit={accept} className="mt-6 space-y-3">
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-[#93B1A8]">Your name (optional)</label>
            <Input
              type="text"
              placeholder="Jane Hero"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 h-11 bg-[#0A2320] border-[#1B4238] text-white"
              data-testid="accept-invite-name"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-[#93B1A8]">Password (8+ chars)</label>
            <Input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="mt-1 h-11 bg-[#0A2320] border-[#1B4238] text-white"
              data-testid="accept-invite-password"
            />
            <p className="mt-1 text-[10px] text-[#93B1A8]">
              If you already have a Money Hero account on this email, your existing password still works — enter anything here.
            </p>
          </div>
          <Button
            type="submit"
            disabled={busy || !token}
            className="w-full h-12 rounded-2xl bg-[#22E07A] hover:bg-[#17C06A] text-[#05160D] font-black uppercase tracking-wider gap-2 ht-outline-green"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="accept-invite-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-5 h-5" strokeWidth={2.8} />}
            Accept &amp; activate
          </Button>
        </form>
      </div>
    </div>
  );
}
